import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon } from "@circleco/compass/components/Icon";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";

import { useTipTapEditorContext } from "../../TipTapEditorContext";
import { CloseButton } from "./CloseButtonCompass";
import { getHostname } from "./helper";
import type { Meta } from "./types";

interface LinkItemProps {
  meta: Meta;
  url: string;
}

export const LinkItemCompass = ({ url, meta }: LinkItemProps) => {
  const { editor, setLinkPreviews, linkPreviews } = useTipTapEditorContext();

  const onRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (linkPreviews && Object.keys(linkPreviews).includes(url)) {
      setLinkPreviews((prev: { [key: string]: boolean }) => ({
        ...prev,
        [url]: false,
      }));
    }
  };

  if (!(url && meta.title)) {
    return null;
  }

  const isEditMode = editor.isEditable;
  const isYoutubeOembed =
    meta?.source === "youtube-oembed" && meta?.thumbnail_url;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={classNames(
        "border-primary bg-primary hover:border-hover hover:bg-hover group relative flex min-w-[12.5rem] max-w-full shrink-0 flex-col rounded-lg border px-4 py-3 sm:w-[19rem]",
        isEditMode
          ? "h-[4.25rem] w-[17.125rem] md:w-[20.5rem]"
          : "w-[17.875rem] md:w-[22.75rem]",
        {
          "max-h-[7.125rem]": !isEditMode && !isYoutubeOembed,
          "max-h-[22.75rem]": !isEditMode && isYoutubeOembed,
        },
      )}
    >
      <div className="text-default flex items-center pb-1.5">
        {meta.favicon ? (
          <img
            loading="eager"
            alt=""
            src={meta.favicon}
            className="mr-2 size-4"
          />
        ) : (
          <Icon name="globus" size="sm" className="mr-2" />
        )}
        <Typography variant="heading-xs" className="truncate">
          {meta.site_name || getHostname(url)}
        </Typography>
      </div>
      {isEditMode ? (
        <div className="mt-0.5 flex w-full flex-row items-center truncate">
          <Typography variant="heading-sm">{meta.title}</Typography>
          {meta.description && (
            <Typography
              variant="body-sm"
              color="secondary"
              className="truncate"
            >
              <span className="px-1">{t("typing_cursor")}</span>
              {meta.description}
            </Typography>
          )}
        </div>
      ) : (
        <>
          <Typography variant="heading-sm" className="mb-1" truncate>
            {meta.title}
          </Typography>
          {meta.description && (
            <Typography
              variant="body-sm"
              color="secondary"
              className="line-clamp-2"
            >
              {meta.description}
            </Typography>
          )}
          {isYoutubeOembed && (
            <div className="relative mt-1">
              <img
                loading="eager"
                src={meta.thumbnail_url}
                alt={meta.title}
                className="h-[185px] w-full rounded-md object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex size-10 items-center justify-center rounded-lg bg-[#191B1F]/40 shadow-lg">
                  <Icon
                    name="chevron-triangle-right"
                    size="lg"
                    color="inverse"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {isEditMode && <CloseButton onRemove={onRemove} />}
    </a>
  );
};
